/**
 * PayPal Pay Later Messages SDK v6 initializer (HTML / auto-bootstrap mode)
 * Docs: https://developer.paypal.com/pay-later/integrate
 *
 * Each <paypal-message> element ships with `auto-bootstrap` plus its
 * configuration as HTML attributes. After the SDK is initialized, PayPal's
 * data layer automatically picks up every element (including ones injected
 * later, e.g. cart drawer re-renders) and refreshes content automatically
 * when the `amount` attribute changes. This file therefore only:
 *   1. creates the SDK instance and the PayPal Messages instance
 *   2. wires the Learn More click handling (not covered by auto-bootstrap)
 *
 * Do NOT call fetchContent()/setContent() on auto-bootstrap elements — the
 * manual and auto modes must not be mixed.
 *
 * buyerCountry is pinned to the storefront market: without it PayPal infers
 * the buyer market from the visitor IP, and an unsupported market/currency
 * pair (e.g. CN IP + USD) returns 422 CONTENT_UNAVAILABLE with no offers.
 */
(function () {
  var config = window.ELEGOO_PAYPAL_MESSAGES_CONFIG;

  if (!config || !config.clientId) {
    console.error(
      "[paypal-messages] aborting: ELEGOO_PAYPAL_MESSAGES_CONFIG missing or has no clientId",
    );
    return;
  }

  var messagesInstance = null;
  var learnMoreInstance = null;

  /**
   * Wait for the v6 core script to expose window.paypal.createInstance.
   * @returns {Promise<void>}
   */
  function waitForCoreSdk() {
    return new Promise(function (resolve, reject) {
      if (window.paypal && typeof window.paypal.createInstance === "function") {
        resolve();
        return;
      }

      var coreScript = document.getElementById("paypal-web-sdk-v6-core");

      if (!coreScript) {
        reject(new Error("PayPal web SDK v6 core script tag is missing"));
        return;
      }

      coreScript.addEventListener("load", function () {
        if (
          window.paypal &&
          typeof window.paypal.createInstance === "function"
        ) {
          resolve();
        } else {
          reject(
            new Error(
              "PayPal web SDK v6 core loaded but createInstance is unavailable",
            ),
          );
        }
      });
      coreScript.addEventListener("error", function () {
        reject(new Error("Failed to load PayPal web SDK v6 core"));
      });
    });
  }

  /**
   * Learn More is not wired automatically even in auto-bootstrap mode.
   * One delegated listener covers static and dynamically injected elements;
   * createLearnMore() resolves synchronously on some SDK builds, hence the
   * Promise.resolve() wrapping.
   */
  function initLearnMore() {
    document.addEventListener("paypal-message-click", function (event) {
      if (!learnMoreInstance || !event.detail) {
        return;
      }

      var target = event.target;
      var message =
        target && target.closest ? target.closest("paypal-message") : null;

      if (!message) {
        return;
      }

      event.preventDefault();
      learnMoreInstance.open(event.detail.config);
    });

    try {
      Promise.resolve(messagesInstance.createLearnMore())
        .then(function (instance) {
          learnMoreInstance = instance;
        })
        .catch(function (error) {
          console.error("[paypal-messages] createLearnMore rejected", error);
        });
    } catch (error) {
      console.error("[paypal-messages] createLearnMore threw", error);
    }
  }

  function init() {
    var messagesOptions = {};

    if (config.currencyCode) {
      messagesOptions.currencyCode = config.currencyCode;
    }

    // Pin the buyer market to the storefront market instead of letting
    // PayPal infer it from the visitor IP. Only honored when the merchant
    // is enabled for cross-border presentment; otherwise ignored.
    if (config.buyerCountry) {
      messagesOptions.buyerCountry = config.buyerCountry;
    }

    return window.paypal
      .createInstance({
        clientId: config.clientId,
        components: ["paypal-messages"],
      })
      .then(function (sdkInstance) {
        messagesInstance = sdkInstance.createPayPalMessages(messagesOptions);

        initLearnMore();
      })
      .catch(function (error) {
        console.error("[paypal-messages] SDK initialization failed", error);
      });
  }

  waitForCoreSdk()
    .then(init)
    .catch(function (error) {
      console.error("[paypal-messages] core SDK unavailable", error);
    });
})();
